/**
 * API origin.
 *
 * The frontend used to call the backend through a Vercel rewrite
 * (`/api/:path*` -> caritas-inventario.fly.dev). That put Vercel in the data
 * path for every request the app makes — auth, inventory, history, uploads,
 * Excel imports — while Fly did all the actual work. Vercel bills that as
 * Fast Origin Transfer plus a Function invocation per request, and their fair
 * use guidelines list "Proxies" as never fair use. The app now talks to Fly
 * directly and Vercel only serves static files.
 *
 * Auth rides in an `Authorization: Bearer` header with the token in
 * localStorage, so going cross-origin costs us nothing: there is no cookie
 * whose SameSite rules would need loosening.
 *
 * In dev this stays empty so requests keep flowing through the Vite proxy to
 * the local API on :8081 (see vite.config.ts).
 */
const configured = import.meta.env.VITE_API_BASE_URL as string | undefined

export const API_BASE = (
  configured ?? (import.meta.env.PROD ? 'https://caritas-inventario.fly.dev' : '')
).replace(/\/+$/, '')

/** Absolute URL for an `/api/...` path. Use for anything fetch does not see. */
export function apiUrl(path: string): string {
  if (!API_BASE) return path
  return path.startsWith('/api/') ? API_BASE + path : path
}

function rewrite(url: string): string {
  // Only same-origin /api/* is redirected; absolute URLs to R2, YouTube, etc.
  // are left exactly as they are.
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/')) {
      return API_BASE + parsed.pathname + parsed.search + parsed.hash
    }
  } catch {
    /* not parseable as a URL — hand it back untouched */
  }
  return url
}

/**
 * Point every `/api/*` fetch at the API origin.
 *
 * There are 60+ call sites across 19 files plus a `fetchJson` wrapper, all
 * using relative paths. Routing them at the fetch layer keeps that one
 * decision in one place instead of threading a base URL through every
 * component, and it covers call sites added later for free.
 */
export function installApiBase(): void {
  if (!API_BASE) return

  const nativeFetch = window.fetch.bind(window)

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') {
      return nativeFetch(rewrite(input), init)
    }
    if (input instanceof URL) {
      return nativeFetch(rewrite(input.toString()), init)
    }
    if (input instanceof Request) {
      const next = rewrite(input.url)
      return next === input.url
        ? nativeFetch(input, init)
        : nativeFetch(new Request(next, input), init)
    }
    return nativeFetch(input as RequestInfo, init)
  }
}
